<script>
import { ref, provide } from 'vue';
// composable
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useLabelSuggestions } from 'dashboard/composables/useLabelSuggestions';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

// components
import ReplyBox from './ReplyBox.vue';
import ActionCenterDialog from './ActionCenterDialog.vue';
import MessageList from 'next/message/MessageList.vue';
import ConversationLabelSuggestion from './conversation/LabelSuggestion.vue';
import UnifiedContactHistory from './conversation/UnifiedContactHistory.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

// stores and apis
import { mapGetters } from 'vuex';

// mixins
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';

// utils
import { emitter } from 'shared/helpers/mitt';
import { getTypingUsersText } from '../../../helper/commons';
import { calculateScrollTop } from './helpers/scrollTopCalculationHelper';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  filterDuplicateSourceMessages,
  getReadMessages,
  getUnreadMessages,
} from 'dashboard/helper/conversationHelper';

// constants
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const ACTION_CENTER_MODAL_EVENT = 'conversation:action-center-modal:open';

export default {
  components: {
    MessageList,
    ReplyBox,
    ActionCenterDialog,
    Banner,
    Spinner,
    UnifiedContactHistory,
    ConversationLabelSuggestion,
  },
  mixins: [inboxMixin],
  setup() {
    const isPopOutReplyBox = ref(false);
    const conversationPanelRef = ref(null);

    const keyboardEvents = {
      Escape: {
        action: () => {
          isPopOutReplyBox.value = false;
        },
      },
    };

    useKeyboardEvents(keyboardEvents);

    const {
      captainTasksEnabled,
      isLabelSuggestionFeatureEnabled,
      getLabelSuggestions,
    } = useLabelSuggestions();

    provide('contextMenuElementTarget', conversationPanelRef);

    return {
      isPopOutReplyBox,
      captainTasksEnabled,
      getLabelSuggestions,
      isLabelSuggestionFeatureEnabled,
      conversationPanelRef,
    };
  },
  data() {
    return {
      isLoadingPrevious: true,
      heightBeforeLoad: null,
      conversationPanel: null,
      hasUserScrolled: false,
      isProgrammaticScroll: false,
      messageSentSinceOpened: false,
      labelSuggestions: [],
      touchStartY: null,
      touchScrollIntent: false,
      mobileReplyFocused: false,
      mobileViewportHeight: null,
      mobileKeyboardOffset: 0,
      mobileLayoutViewportHeight: null,
      mobileViewportFrame: null,
      mobileFocusResetTimer: null,
      mobileViewportStableTimer: null,
    };
  },

  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUserId: 'getCurrentUserID',
      listLoadingStatus: 'getAllMessagesLoaded',
      currentAccountId: 'getCurrentAccountId',
    }),
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    shouldShowLabelSuggestions() {
      return (
        this.isOpen &&
        this.captainTasksEnabled &&
        this.isLabelSuggestionFeatureEnabled &&
        !this.messageSentSinceOpened
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    currentContactId() {
      return (
        this.currentChat?.meta?.sender?.id ||
        this.currentChat?.meta?.sender?.contact_id ||
        null
      );
    },
    currentContact() {
      if (!this.currentContactId) {
        return {};
      }

      return this.$store.getters['contacts/getContact'](this.currentContactId) || {};
    },
    contactCustomAttributes() {
      return this.currentContact?.custom_attributes || {};
    },
    activeBookingCount() {
      return Number(this.contactCustomAttributes.active_booking_count || 0) || 0;
    },
    activeBookingIds() {
      return String(this.contactCustomAttributes.active_booking_ids || '').trim();
    },
    activeBookingSummaryLines() {
      return String(this.contactCustomAttributes.active_bookings_summary || '')
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);
    },
    mobileViewportStyle() {
      if (!this.mobileViewportHeight) {
        return {};
      }

      return {
        '--dv-chatwoot-visual-height': `${this.mobileViewportHeight}px`,
        '--dv-chatwoot-keyboard-offset': `${this.mobileKeyboardOffset || 0}px`,
      };
    },
    isMobileReplyKeyboardOpen() {
      return (
        this.mobileReplyFocused &&
        !!this.mobileViewportHeight &&
        this.isTouchMobileViewport()
      );
    },
    currentInboxName() {
      return this.inbox?.name || this.currentChat?.meta?.channel || 'Conversation';
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList;
      return userList.length !== 0;
    },
    typingUserNames() {
      const userList = this.typingUsersList;
      if (this.isAnyoneTyping) {
        const [i18nKey, params] = getTypingUsersText(userList);
        return this.$t(i18nKey, params);
      }

      return '';
    },
    getMessages() {
      const messages = this.currentChat.messages || [];
      if (this.isAWhatsAppChannel) {
        return filterDuplicateSourceMessages(messages);
      }
      return messages;
    },
    readMessages() {
      return getReadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    unReadMessages() {
      return getUnreadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    shouldShowSpinner() {
      return (
        (this.currentChat && this.currentChat.dataFetched === undefined) ||
        (!this.listLoadingStatus && this.isLoadingPrevious)
      );
    },
    // Check there is a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const { additional_attributes: additionalAttributes = {} } = this.inbox;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return (
        this.inbox.channel_type === INBOX_TYPES.FB &&
        additionalAttributes.type === 'instagram_direct_message' &&
        instagramInbox
      );
    },

    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
            agent_reply_time_window: agentReplyTimeWindow,
          } = additionalAttributes;
          return (
            agentReplyTimeWindowMessage ||
            this.$t('CONVERSATION.API_HOURS_WINDOW', {
              hours: agentReplyTimeWindow,
            })
          );
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (this.isAFacebookInbox || this.isAnInstagramChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (this.isAWhatsAppCloudChannel) {
        return REPLY_POLICY.WHATSAPP_CLOUD;
      }
      if (this.isATiktokChannel) {
        return REPLY_POLICY.TIKTOK;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAnInstagramChannel
      ) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (this.isATiktokChannel) {
        return this.$t('CONVERSATION.48_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    unreadMessageCount() {
      return this.currentChat.unread_count || 0;
    },
    unreadMessageLabel() {
      const count =
        this.unreadMessageCount > 9 ? '9+' : this.unreadMessageCount;
      const label =
        this.unreadMessageCount > 1
          ? 'CONVERSATION.UNREAD_MESSAGES'
          : 'CONVERSATION.UNREAD_MESSAGE';
      return `${count} ${this.$t(label)}`;
    },
    inboxSupportsReplyTo() {
      const incoming = this.inboxHasFeature(INBOX_FEATURES.REPLY_TO);
      const outgoing =
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO_OUTGOING) &&
        !this.is360DialogWhatsAppChannel;

      return { incoming, outgoing };
    },
  },

  watch: {
    currentChat(newChat, oldChat) {
      if (newChat.id === oldChat.id) {
        return;
      }
      this.fetchAllAttachmentsFromCurrentChat();
      this.fetchSuggestions();
      this.messageSentSinceOpened = false;
      this.hasUserScrolled = false;
    },
  },

  created() {
    emitter.on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    // when a message is sent we set the flag to true this hides the label suggestions,
    // until the chat is changed and the flag is reset in the watch for currentChat
    emitter.on(BUS_EVENTS.MESSAGE_SENT, () => {
      this.messageSentSinceOpened = true;
    });
  },

  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
    this.fetchSuggestions();
    emitter.on(ACTION_CENTER_MODAL_EVENT, this.openActionCenterModal);
    this.bindMobileViewportListeners();
  },

  unmounted() {
    this.removeBusListeners();
    this.removeScrollListener();
    emitter.off(ACTION_CENTER_MODAL_EVENT, this.openActionCenterModal);
    this.unbindMobileViewportListeners();
  },

  methods: {
    openActionCenterModal() {
      if (this.currentContactId) {
        this.$store.dispatch('contacts/show', { id: this.currentContactId }).catch(() => {});
      }

      this.$refs.actionCenterDialog?.open();
    },
    async fetchSuggestions() {
      // start empty, this ensures that the label suggestions are not shown
      this.labelSuggestions = [];

      if (this.isLabelSuggestionDismissed()) {
        return;
      }

      // Early exit if conversation already has labels - no need to suggest more
      const existingLabels = this.currentChat?.labels || [];
      if (existingLabels.length > 0) return;

      if (!this.captainTasksEnabled || !this.isLabelSuggestionFeatureEnabled) {
        return;
      }

      this.labelSuggestions = await this.getLabelSuggestions();

      // once the labels are fetched, we need to scroll to bottom
      // but we need to wait for the DOM to be updated
      // so we use the nextTick method
      this.$nextTick(() => {
        // this param is added to route, telling the UI to navigate to the message
        // it is triggered by the SCROLL_TO_MESSAGE method
        // see setActiveChat on ConversationView.vue for more info
        const { messageId } = this.$route.query;

        // only trigger the scroll to bottom if the user has not scrolled
        // and there's no active messageId that is selected in view
        if (!messageId && !this.hasUserScrolled) {
          this.scrollToBottom();
        }
      });
    },
    isLabelSuggestionDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.currentChat.id
      );
    },
    fetchAllAttachmentsFromCurrentChat() {
      this.$store.dispatch('fetchAllAttachments', this.currentChat.id);
    },
    removeBusListeners() {
      emitter.off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    },
    onScrollToMessage({ messageId = '' } = {}) {
      this.$nextTick(() => {
        const messageElement = document.getElementById('message' + messageId);
        if (messageElement) {
          this.isProgrammaticScroll = true;
          messageElement.scrollIntoView({ behavior: 'smooth' });
          this.fetchPreviousMessages();
        } else {
          this.scrollToBottom();
        }
      });
      this.makeMessagesRead();
    },
    addScrollListener() {
      this.conversationPanel = this.$el.querySelector('.conversation-panel');
      this.setScrollParams();
      this.conversationPanel.addEventListener('scroll', this.handleScroll);
      this.conversationPanel.addEventListener('touchstart', this.recordTouchStart, {
        passive: true,
      });
      this.conversationPanel.addEventListener('touchmove', this.recordTouchMove, {
        passive: true,
      });
      this.$nextTick(() => this.scrollToBottom());
      this.isLoadingPrevious = false;
    },
    removeScrollListener() {
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
      this.conversationPanel.removeEventListener('touchstart', this.recordTouchStart);
      this.conversationPanel.removeEventListener('touchmove', this.recordTouchMove);
    },
    scrollToBottom() {
      this.isProgrammaticScroll = true;
      let relevantMessages = [];

      // label suggestions are not part of the messages list
      // so we need to handle them separately
      let labelSuggestions =
        this.conversationPanel.querySelector('.label-suggestion');

      // if there are unread messages, scroll to the first unread message
      if (this.unreadMessageCount > 0) {
        // capturing only the unread messages
        relevantMessages =
          this.conversationPanel.querySelectorAll('.message--unread');
      } else if (labelSuggestions) {
        // when scrolling to the bottom, the label suggestions is below the last message
        // so we scroll there if there are no unread messages
        // Unread messages always take the highest priority
        relevantMessages = [labelSuggestions];
      } else {
        // if there are no unread messages or label suggestion, scroll to the last message
        // capturing last message from the messages list
        relevantMessages = Array.from(
          this.conversationPanel.querySelectorAll('.message--read')
        ).slice(-1);
      }

      this.conversationPanel.scrollTop = calculateScrollTop(
        this.conversationPanel.scrollHeight,
        this.$el.scrollHeight,
        relevantMessages
      );
    },
    setScrollParams() {
      this.heightBeforeLoad = this.conversationPanel.scrollHeight;
      this.scrollTopBeforeLoad = this.conversationPanel.scrollTop;
    },
    handleUnifiedHistoryLoaded() {
      if (!this.hasUserScrolled) {
        this.$nextTick(() => this.scrollToBottom());
      }
    },

    async fetchPreviousMessages(scrollTop = 0) {
      this.setScrollParams();
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before: this.currentChat.messages[0].id,
          });
          const heightDifference =
            this.conversationPanel.scrollHeight - this.heightBeforeLoad;
          this.conversationPanel.scrollTop =
            this.scrollTopBeforeLoad + heightDifference;
          this.setScrollParams();
        } catch (error) {
          // Ignore Error
        } finally {
          this.isLoadingPrevious = false;
        }
      }
    },

    recordTouchStart(event) {
      this.touchStartY = event.touches?.[0]?.clientY ?? null;
      this.touchScrollIntent = false;
    },

    recordTouchMove(event) {
      if (this.touchStartY === null) {
        return;
      }

      const currentY = event.touches?.[0]?.clientY ?? this.touchStartY;
      if (Math.abs(currentY - this.touchStartY) < 8) {
        return;
      }

      this.touchScrollIntent = true;
      this.hasUserScrolled = true;
      this.isProgrammaticScroll = false;
    },

    handleScroll(e) {
      if (this.isProgrammaticScroll) {
        // Reset the flag
        this.isProgrammaticScroll = false;
      } else {
        this.hasUserScrolled = true;
      }
      if (this.touchScrollIntent) {
        this.hasUserScrolled = true;
        this.touchScrollIntent = false;
      }
      emitter.emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
      this.fetchPreviousMessages(e.target.scrollTop);
    },

    makeMessagesRead() {
      this.$store.dispatch('markMessagesRead', { id: this.currentChat.id });
    },
    async handleMessageRetry(message) {
      if (!message) return;
      const payload = useSnakeCase(message);
      await this.$store.dispatch('sendMessageWithData', payload);
    },
    bindMobileViewportListeners() {
      document.addEventListener('focusin', this.handleMobileFocusIn, true);
      document.addEventListener('focusout', this.handleMobileFocusOut, true);
      window.addEventListener('resize', this.scheduleMobileViewportUpdate, {
        passive: true,
      });
      window.addEventListener(
        'orientationchange',
        this.handleMobileOrientationChange,
        { passive: true }
      );

      if (window.visualViewport) {
        window.visualViewport.addEventListener(
          'resize',
          this.scheduleMobileViewportUpdate,
          { passive: true }
        );
        window.visualViewport.addEventListener(
          'scroll',
          this.scheduleMobileViewportUpdate,
          { passive: true }
        );
      }

      this.scheduleMobileViewportUpdate();
    },
    unbindMobileViewportListeners() {
      document.removeEventListener('focusin', this.handleMobileFocusIn, true);
      document.removeEventListener('focusout', this.handleMobileFocusOut, true);
      window.removeEventListener('resize', this.scheduleMobileViewportUpdate);
      window.removeEventListener(
        'orientationchange',
        this.handleMobileOrientationChange
      );

      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          this.scheduleMobileViewportUpdate
        );
        window.visualViewport.removeEventListener(
          'scroll',
          this.scheduleMobileViewportUpdate
        );
      }

      if (this.mobileViewportFrame) {
        cancelAnimationFrame(this.mobileViewportFrame);
      }
      if (this.mobileFocusResetTimer) {
        clearTimeout(this.mobileFocusResetTimer);
      }
      if (this.mobileViewportStableTimer) {
        clearTimeout(this.mobileViewportStableTimer);
      }
    },
    isTouchMobileViewport() {
      if (typeof window === 'undefined') {
        return false;
      }

      return (
        window.matchMedia?.('(hover: none) and (pointer: coarse)').matches ||
        window.innerWidth <= 768
      );
    },
    isReplyComposerTarget(target) {
      if (!target) {
        return false;
      }

      const composerNode = target.closest?.(
        [
          '.reply-box',
          '.reply-bottom-panel',
          '.ProseMirror',
          '.ProseMirror-woot-style',
          '.message-editor',
          '[contenteditable="true"]',
          '[role="textbox"]',
          'textarea',
          'input',
        ].join(',')
      );

      return Boolean(
        composerNode && this.$refs.messagesViewport?.contains(composerNode)
      );
    },
    isReplyEditableTarget(target) {
      if (!target) {
        return false;
      }

      const editableNode = target.closest?.(
        [
          '.ProseMirror',
          '.ProseMirror-woot-style',
          '.message-editor',
          '[contenteditable="true"]',
          'textarea',
          'input',
        ].join(',')
      );

      return Boolean(
        editableNode && this.$refs.messagesViewport?.contains(editableNode)
      );
    },
    scheduleMobileViewportUpdate() {
      if (this.mobileViewportFrame) {
        cancelAnimationFrame(this.mobileViewportFrame);
      }

      this.mobileViewportFrame = requestAnimationFrame(() => {
        this.mobileViewportFrame = null;
        this.updateMobileViewportState();
      });
    },
    handleMobileOrientationChange() {
      this.mobileLayoutViewportHeight = null;
      this.scheduleMobileViewportUpdate();
    },
    handleMobileComposerPointerDown(event) {
      if (!this.isReplyEditableTarget(event.target)) {
        return;
      }

      this.mobileReplyFocused = true;
      this.scheduleMobileViewportUpdate();
      window.setTimeout(() => this.scheduleMobileViewportUpdate(), 80);
      window.setTimeout(() => this.keepReplyBoxVisible(), 180);
    },
    updateMobileViewportState() {
      if (!this.isTouchMobileViewport()) {
        this.mobileReplyFocused = false;
        this.mobileViewportHeight = null;
        this.mobileKeyboardOffset = 0;
        return;
      }

      const visualViewport = window.visualViewport;
      const layoutHeight = Math.round(
        Math.max(
          window.innerHeight || 0,
          document.documentElement.clientHeight || 0
        )
      );
      const visualHeight = Math.round(
        visualViewport?.height ||
          window.innerHeight ||
          document.documentElement.clientHeight ||
          0
      );
      const visualTop = Math.round(visualViewport?.offsetTop || 0);
      if (!visualHeight) {
        this.mobileViewportHeight = null;
        this.mobileKeyboardOffset = 0;
        return;
      }

      this.mobileLayoutViewportHeight = Math.max(
        this.mobileLayoutViewportHeight || 0,
        layoutHeight,
        visualHeight
      );
      const knownLayoutHeight = this.mobileLayoutViewportHeight || layoutHeight;

      const root = this.$refs.messagesViewport;
      const rootTop = root?.getBoundingClientRect
        ? Math.max(0, Math.round(root.getBoundingClientRect().top - visualTop))
        : 0;
      const keyboardOffset = Math.max(
        0,
        knownLayoutHeight - Math.round(visualTop + visualHeight)
      );

      this.mobileKeyboardOffset = keyboardOffset;
      this.mobileViewportHeight = Math.max(260, visualHeight - rootTop);
    },
    handleMobileFocusIn(event) {
      if (!this.isReplyComposerTarget(event.target)) {
        return;
      }

      this.mobileReplyFocused = true;
      this.scheduleMobileViewportUpdate();
      this.keepReplyBoxVisible();
      window.setTimeout(() => this.keepReplyBoxVisible(), 120);
      window.setTimeout(() => this.keepReplyBoxVisible(), 320);
      window.setTimeout(() => this.scheduleMobileViewportUpdate(), 320);
      window.setTimeout(() => this.keepReplyBoxVisible(), 520);

      if (this.mobileViewportStableTimer) {
        clearTimeout(this.mobileViewportStableTimer);
      }

      this.mobileViewportStableTimer = window.setTimeout(() => {
        this.scheduleMobileViewportUpdate();
        this.keepReplyBoxVisible();
      }, 720);
    },
    handleMobileFocusOut() {
      if (this.mobileFocusResetTimer) {
        clearTimeout(this.mobileFocusResetTimer);
      }

      this.mobileFocusResetTimer = window.setTimeout(() => {
        if (this.isReplyComposerTarget(document.activeElement)) {
          return;
        }

        this.mobileReplyFocused = false;
        this.scheduleMobileViewportUpdate();
      }, 120);
    },
    keepReplyBoxVisible() {
      if (!this.isTouchMobileViewport()) {
        return;
      }

      this.$nextTick(() => {
        const root = this.$refs.messagesViewport;
        const replyBox = root?.querySelector('.reply-box');
        if (!replyBox) {
          return;
        }

        replyBox.scrollIntoView({ block: 'end', inline: 'nearest' });

        const panel = root?.querySelector('.conversation-panel');
        if (panel && !this.hasUserScrolled) {
          panel.scrollTop = panel.scrollHeight;
        }
      });
    },
  },
};
</script>

<template>
  <div
    ref="messagesViewport"
    class="dv-chatwoot-messages-viewport flex flex-col justify-between flex-grow h-full min-w-0 m-0"
    :class="{ 'is-mobile-keyboard-open': isMobileReplyKeyboardOpen }"
    :style="mobileViewportStyle"
    @pointerdown.capture="handleMobileComposerPointerDown"
    @touchstart.capture="handleMobileComposerPointerDown"
  >
    <Banner
      v-if="!currentChat.can_reply"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="replyWindowBannerMessage"
      :href-link="replyWindowLink"
      :href-link-text="replyWindowLinkText"
    />
    <Banner
      v-else-if="hasDuplicateInstagramInbox"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="$t('CONVERSATION.OLD_INSTAGRAM_INBOX_REPLY_BANNER')"
    />
    <MessageList
      ref="conversationPanelRef"
      class="conversation-panel flex-shrink flex-grow basis-px flex flex-col overflow-y-auto relative h-full m-0 pb-4"
      :current-user-id="currentUserId"
      :first-unread-id="unReadMessages[0]?.id"
      :is-an-email-channel="isAnEmailChannel"
      :inbox-supports-reply-to="inboxSupportsReplyTo"
      :messages="getMessages"
      @retry="handleMessageRetry"
    >
      <template #beforeAll>
        <transition name="slide-up">
          <!-- eslint-disable-next-line vue/require-toggle-inside-transition -->
          <li
            class="min-h-[4rem] flex flex-shrink-0 flex-grow-0 items-center flex-auto justify-center max-w-full mt-0 mr-0 mb-1 ml-0 relative first:mt-auto last:mb-0"
          >
            <Spinner v-if="shouldShowSpinner" class="text-n-brand" />
          </li>
        </transition>
        <UnifiedContactHistory
          v-if="currentContactId"
          :key="`${currentContactId}-${currentChat.id}`"
          :contact-id="currentContactId"
          :current-conversation-id="currentChat.id"
          :current-inbox-name="currentInboxName"
          :current-user-id="currentUserId"
          @loaded="handleUnifiedHistoryLoaded"
        />
      </template>
      <template #unreadBadge>
        <li
          v-show="unreadMessageCount != 0"
          class="list-none flex justify-center items-center"
        >
          <span
            class="shadow-lg rounded-full bg-n-brand text-white text-xs font-medium my-2.5 mx-auto px-2.5 py-1.5"
          >
            {{ unreadMessageLabel }}
          </span>
        </li>
      </template>
      <template #after>
        <ConversationLabelSuggestion
          v-if="shouldShowLabelSuggestions"
          :suggested-labels="labelSuggestions"
          :chat-labels="currentChat.labels"
          :conversation-id="currentChat.id"
        />
      </template>
    </MessageList>
    <div
      class="dv-chatwoot-composer-wrap flex relative flex-col shrink-0"
      :class="{
        'modal-mask': isPopOutReplyBox,
        'bg-n-surface-1': !isPopOutReplyBox,
      }"
    >
      <div
        v-if="isAnyoneTyping"
        class="dv-typing-indicator absolute inset-x-0 -top-9 z-20 flex justify-center px-4 pointer-events-none"
      >
        <div
          class="dv-typing-indicator__pill inline-flex max-w-full items-center gap-2 rounded-full border border-n-weak bg-white px-3.5 py-1.5 text-xs font-medium text-n-slate-11 shadow-lg backdrop-blur dark:border-n-weak/80 dark:bg-n-solid-3"
          :title="typingUserNames"
        >
          <span class="dv-typing-indicator__text truncate">
            {{ typingUserNames }}
          </span>
          <span class="dv-typing-indicator__dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        </div>
      </div>
      <ReplyBox
        :pop-out-reply-box="isPopOutReplyBox"
        @update:pop-out-reply-box="isPopOutReplyBox = $event"
      />
      <ActionCenterDialog
        ref="actionCenterDialog"
        :conversation-id="currentChat.id"
        :contact="currentContact"
        :current-chat="currentChat"
        :channel-type="currentChat.meta?.channel || ''"
        :active-booking-count="activeBookingCount"
        :active-booking-ids="activeBookingIds"
        :active-booking-summary-lines="activeBookingSummaryLines"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.modal-mask {
  @apply fixed;

  &::v-deep {
    .ProseMirror-woot-style {
      @apply max-h-[25rem];
    }

    .reply-box {
      @apply border border-n-weak max-w-[75rem] w-[70%];

      &.is-private {
        @apply dark:border-n-amber-3/30 border-n-amber-12/5;
      }
    }

    .reply-box .reply-box__top {
      @apply relative min-h-[27.5rem];
    }

    .reply-box__top .input {
      @apply min-h-[27.5rem];
    }

    .emoji-dialog {
      @apply absolute ltr:left-auto rtl:right-auto bottom-1;
    }
  }
}

.conversation-panel {
  overscroll-behavior-y: contain;
}

.dv-typing-indicator {
  line-height: 1;
}

.dv-typing-indicator__pill {
  min-height: 2rem;
  max-width: min(34rem, calc(100vw - 2rem));
}

.dv-typing-indicator__text {
  min-width: 0;
}

.dv-typing-indicator__dots {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 0.1875rem;

  span {
    width: 0.3125rem;
    height: 0.3125rem;
    border-radius: 9999px;
    background: currentColor;
    opacity: 0.45;
    animation: dvTypingPulse 1.1s infinite ease-in-out;

    &:nth-child(2) {
      animation-delay: 0.16s;
    }

    &:nth-child(3) {
      animation-delay: 0.32s;
    }
  }
}

@keyframes dvTypingPulse {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.35;
  }

  40% {
    transform: translateY(-0.125rem);
    opacity: 0.9;
  }
}

.dv-chatwoot-messages-viewport.is-mobile-keyboard-open {
  height: var(--dv-chatwoot-visual-height, 100dvh) !important;
  max-height: var(--dv-chatwoot-visual-height, 100dvh) !important;
  min-height: 0;
  overflow: hidden;
  overscroll-behavior: contain;
  transition: height 120ms ease;

  .conversation-panel {
    min-height: 0;
    flex: 1 1 auto;
    padding-bottom: 0.5rem;
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-y: contain;
  }

  .dv-chatwoot-composer-wrap {
    flex: 0 0 auto;
    max-height: none;
    overflow: visible;
  }

  &::v-deep {
    .reply-box {
      margin-bottom: max(env(safe-area-inset-bottom), 0.375rem);
    }

    .reply-box__top {
      max-height: none;
      overflow: visible;
    }

    .reply-box__top .input {
      min-height: 5.25rem;
    }

    .ProseMirror-woot-style {
      min-height: 5rem;
      max-height: clamp(5rem, calc(var(--dv-chatwoot-visual-height, 100dvh) - 15rem), 7.5rem);
      overflow-y: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }
  }
}

@media (hover: none) and (pointer: coarse) {
  .dv-chatwoot-messages-viewport {
    min-height: 0;
  }

  .conversation-panel {
    min-height: 0;
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-y: contain;
  }
}
</style>
